import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { mockPlants, mockTasks, mockZones } from '../../data/mockData';
import { SectionBadge } from '../BrandAssets';

const { FiDroplet, FiAlertCircle, FiMap, FiSun, FiThermometer, FiWind } = FiIcons;

export default function DashboardView() {
  const urgentTasks = mockTasks.filter(t => t.urgent);
  const overallHydration = Math.round(mockPlants.reduce((acc, p) => acc + p.hydration, 0) / mockPlants.length);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 pb-24 md:pb-6">
      {/* HUD Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-garden-dark p-8 rounded-2xl border border-garden-base relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-lime/10 rounded-full blur-[100px] -z-10 translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] -z-10 -translate-x-1/2 translate-y-1/2"></div>
        
        <div className="space-y-1">
          <SectionBadge label="System Telemetry" />
          <h2 className="text-4xl font-black text-clay tracking-tight mt-2">OPERATIONAL</h2>
          <p className="text-clay-muted text-sm font-medium">All biological sensors reporting within normal parameters.</p>
        </div>

        <div className="flex items-center gap-8 bg-garden-darkest/50 p-4 rounded-2xl border border-garden-base/50 backdrop-blur">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-garden-base flex items-center justify-center text-lime border border-lime/20">
              <SafeIcon icon={FiThermometer} className="text-xl" />
            </div>
            <div>
              <p className="text-[10px] text-clay-dark font-mono uppercase tracking-widest">Ambient</p>
              <p className="text-2xl font-mono text-lime font-bold">24.2<span className="text-sm">°C</span></p>
            </div>
          </div>
          <div className="w-px h-12 bg-garden-base"></div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-garden-base flex items-center justify-center text-blue-400 border border-blue-400/20">
              <SafeIcon icon={FiWind} className="text-xl" />
            </div>
            <div>
              <p className="text-[10px] text-clay-dark font-mono uppercase tracking-widest">Humidity</p>
              <p className="text-2xl font-mono text-blue-400 font-bold">62<span className="text-sm">%</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Hydration Widget */}
          <div className="glass-panel p-8 rounded-2xl border border-garden-base">
            <div className="flex justify-between items-center mb-10">
              <h3 className="text-xl font-bold text-clay flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <SafeIcon icon={FiDroplet} className="text-blue-400" />
                </div>
                Hydration Equilibrium
              </h3>
              <span className="text-[10px] font-mono text-clay-dark bg-garden-darkest px-2 py-1 rounded border border-garden-base">RT_STREAM: ACTIVE</span>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="relative w-40 h-40 shrink-0">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="45" stroke="#142E1B" strokeWidth="8" fill="transparent" />
                  <motion.circle 
                    cx="50" cy="50" r="45" 
                    stroke="#A3E635" strokeWidth="8" 
                    fill="transparent"
                    strokeDasharray="283"
                    initial={{ strokeDashoffset: 283 }}
                    animate={{ strokeDashoffset: 283 - (283 * overallHydration) / 100 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-mono font-black text-lime leading-none">{overallHydration}</span>
                  <span className="text-[10px] font-mono text-clay-dark uppercase">SYSTEM %</span>
                </div>
              </div>

              <div className="flex-1 grid grid-cols-2 gap-4 w-full">
                {mockPlants.slice(0, 4).map(plant => (
                  <div key={plant.id} className="bg-garden-dark/50 p-4 rounded-xl border border-garden-base group hover:border-lime/30 transition-all">
                    <p className="text-xs font-bold text-clay truncate mb-3">{plant.name}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 h-1 bg-garden-darkest rounded-full overflow-hidden mr-3">
                        <div className="h-full bg-lime" style={{ width: `${plant.hydration}%` }}></div>
                      </div>
                      <span className="text-[10px] font-mono text-lime">{plant.hydration}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Priority Actions */}
          <div className="glass-panel p-8 rounded-2xl border border-garden-base">
            <h3 className="text-xl font-bold text-clay flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                <SafeIcon icon={FiAlertCircle} className="text-red-400" />
              </div>
              Critical Maintenance
            </h3>
            <div className="space-y-4">
              {urgentTasks.map(task => (
                <div key={task.id} className="flex items-center justify-between p-5 bg-garden-darkest border border-red-500/20 rounded-2xl group hover:border-red-500/50 transition-all">
                  <div className="flex items-center gap-4">
                    <button className="w-6 h-6 rounded-lg border-2 border-garden-base flex items-center justify-center hover:border-lime hover:bg-lime/10 transition-all">
                    </button>
                    <span className="text-clay font-bold text-sm">{task.title}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono text-red-400 bg-red-400/10 px-3 py-1 rounded-full border border-red-400/20 uppercase tracking-tighter">
                      PRIORITY {task.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Zones Sidebar */}
        <div className="space-y-6">
          <div className="glass-panel p-8 rounded-2xl border border-garden-base h-full">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-bold text-clay flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <SafeIcon icon={FiMap} className="text-purple-400" />
                </div>
                Active Zones
              </h3>
              <button className="text-[10px] font-mono text-lime hover:underline tracking-widest">MAP_MOD</button>
            </div>
            
            <div className="space-y-5">
              {mockZones.map(zone => (
                <div key={zone.id} className="relative p-5 bg-garden-darkest/50 rounded-2xl border border-garden-base group hover:border-lime/20 transition-all overflow-hidden">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h4 className="text-sm font-black text-clay uppercase tracking-tight">{zone.name}</h4>
                      <p className="text-[9px] font-mono text-clay-dark mt-1">SYS_ID: {zone.id.toUpperCase()}</p>
                    </div>
                    <span className="text-[9px] font-mono bg-garden-base px-2 py-0.5 rounded text-clay-muted uppercase">
                      {zone.type}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-end">
                    <div className="flex items-center gap-2">
                       <SafeIcon icon={FiSun} className="text-yellow-400 text-sm" />
                       <span className="text-[10px] font-mono text-clay-muted">{zone.light} Exposure</span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-mono text-lime font-bold">
                        {zone.current}<span className="text-clay-dark mx-0.5">/</span>{zone.capacity}
                      </p>
                      <p className="text-[8px] font-mono text-clay-dark uppercase">Capacity</p>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 w-full h-1 bg-garden-base/30">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${(zone.current / zone.capacity) * 100}%` }}
                      className={`h-full ${zone.current >= zone.capacity ? 'bg-red-400' : 'bg-lime shadow-[0_0_8px_rgba(163,230,53,0.4)]'}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}